import { Fragment } from 'react';
import {
  BottomSheetContainer,
  type ColorKeys,
  Colors,
  createUseBottomSheetModal,
  Flex,
  IconCamera,
  IconClose,
  IconMyalbum,
  IconTrash,
  Typography,
} from '@banhala/ui-kit';
import { isWeb } from 'shared/src/utils/isWeb';

export type AttachPhotoAction = 'album' | 'camera' | 'reset';

interface AttachPhotoOption {
  action: AttachPhotoAction;
  label: string;
  Icon: typeof IconCamera;
  color: ColorKeys;
  showDividerAbove?: boolean;
}

interface AttachPhotoBottomSheetParam {
  hasCurrentPhoto: boolean;
}

const ALBUM_OPTION: AttachPhotoOption = {
  action: 'album',
  label: '사진첩',
  Icon: IconMyalbum,
  color: 'content_primary',
};

const CAMERA_OPTION: AttachPhotoOption = {
  action: 'camera',
  label: '카메라',
  Icon: IconCamera,
  color: 'content_primary',
};

// 웹은 <input capture> 가 일관되게 동작하지 않아 카메라 옵션을 제외한다.
const BASE_OPTIONS: AttachPhotoOption[] = isWeb
  ? [ALBUM_OPTION]
  : [ALBUM_OPTION, CAMERA_OPTION];

const RESET_OPTION: AttachPhotoOption = {
  action: 'reset',
  label: '현재 사진 삭제',
  Icon: IconTrash,
  color: 'content_negative',
  showDividerAbove: true,
};

export const useAttachPhotoBottomSheet = createUseBottomSheetModal<
  { action: AttachPhotoAction },
  AttachPhotoBottomSheetParam
>(({ confirm, cancel, param }) => {
  const options = param.hasCurrentPhoto
    ? [...BASE_OPTIONS, RESET_OPTION]
    : BASE_OPTIONS;

  return (
    <BottomSheetContainer>
      <Flex height={48} justifyContent={'center'} alignItems={'center'}>
        <Typography type={'subtitle1'} color={'content_primary'}>
          사진 첨부
        </Typography>
        <Flex
          position={'absolute'}
          right={8}
          top={0}
          bottom={0}
          justifyContent={'center'}
          padding={{ horizontal: 8 }}
          onPress={cancel}
        >
          <IconClose size={20} />
        </Flex>
      </Flex>
      {options.map(({ action, label, Icon, color, showDividerAbove }) => (
        <Fragment key={action}>
          {showDividerAbove && <Divider />}
          <Flex
            horizontal
            alignItems={'center'}
            gap={12}
            padding={{ horizontal: 16, vertical: 16 }}
            onPress={() => confirm({ action })}
          >
            <Icon size={20} fill={Colors[color]} />
            <Typography type={'body1'} color={color}>
              {label}
            </Typography>
          </Flex>
        </Fragment>
      ))}
    </BottomSheetContainer>
  );
});

const Divider = () => <Flex height={1} backgroundColor={'bg_tertiary'} />;
